
/** @typedef {import('headlight-configurable-application').Pict} Pict */
const EntityFilterEngineProvider = require('./PictProvider-EntityFilterEngine.js');
// The display-name resolution order, shared with the Picker stack's Read/Print display so the
// Select2 -> Picker migration cannot change what a field paints.
const libEntityDisplayName = require('./_EntityDisplayName.js');
const libSharedEntityRead = require('./_SharedEntityRead.js');

/**
 * @typedef {Object} Select2AjaxOptions
 * @property {number} [PageSize] - The number of items to get per page. Default is 50.
 * @property {Object} [Input] - The input object. Some defaults or fallbacks will be pulled from this if provided.
 * @property {string[]} [SearchFields] - The fields to search for the search term in. Default is ['Name'].
 * @property {(record: any) => string} [GetRecordID] - A function to get the record ID from the entity. Default is the database ID.
 * @property {(record: any) => string} [GetRecordName] - A function to get the record name from the entity. This is what is shown in the search results and selection. Defaults to Record.Name.
 * @property {(record: any) => string} [GetRecordUserFacingID] - A function to get the user facing record ID from the entity record. If provided, will decorate the selection with this ID (in addition to the name).
 * @property {() => Select2Item} [GetPinnedItem] - A function to get the pinned item. If provided, will be the first item in the list.
 * @property {() => Array<any>} [GetPriorityIDs] - Ids floated to the top of the first browse page, most-preferred first. Nothing is removed and a search term suppresses the reorder.
 * @property {(pFilters: string[], pParams: any, pBackoff?: string[]) => string[]} [HandleSearchFilters] - A function to handle the search filters. Return the (mandatory) filters to be used; push any back-off-able clauses into pBackoff. pParams is the Select2 params object for the search.
 * @property {string} [URLPrefix] - Optional prefix prepended to the entity REST path.
 * @property {number} [MinimumInputLengthForSearch] - The minimum input length for search. Default is from panels view, or ultimate fallback of 2.
 * @property {boolean} [Tags] - Whether or not to allow free-entry / dynamic option creation.
 * @property {boolean} [AllowClear] - Whether or not to allow clearing the selection. Default is true.
 * @property {string} [SearchPlaceholder] - The placeholder for the search input. Placeholder from input.PictForm is preferred over this.
 * @property {string} [SearchFallbackPlaceholder] - The fallback placeholder for the search input. Default is `Select ${pEntityType} records...`.
 * @property {string} [Width] - The width of the select2 input. Default is '100%'.
 * @property {boolean} [ApplySort] - Should sorting be applied to the filtering.
 * @property {string} [JoinEntity] - An Entity to Join to.
 * @property {boolean} [JoinEntityFirst] - Whether the Joined Entities name should come first. Defaults to true.
 * @property {string} [JoinField] - The field to join to on the Entity. default is ID<Entity>.
 * @property {boolean} [Disabled] - Whether the select is disabled.
 * @property {boolean} [Multiple] - Whether the select allows multiple selections.
 * @property {string} [EntityDisplayField] - An alternate field to use when displaying an entity.
 * @property {string} [JoinEntityDisplayField] - An alternate field to use when displaying the joined entity.
 * @property {boolean} [EntityTagLast] - Whether the entity tag should be displayed last in the display template, if an EntityTag is used.
 * @property {number} [RowIndex] - For a tabular column picker: this row's index, so a DynamicFilters `FromRowField` resolves against this row's sibling cell.
 * @property {Object} [View] - For a tabular column picker: the form view, providing the marshal destination a `FromRowField` reads from.
 */

/**
 * @typedef {Object} Select2Item
 * @property {string} id - The ID of the item.
 * @property {string} text - The display text of the item.
 */

/**
 * @typedef {Object} EntityDisplayResult
 * @property {Record<string, any>} Record - The entity record, exactly as the server returned it.
 * @property {string} DisplayName - The display text computed for the record.
 */

class Select2UtilProvider extends EntityFilterEngineProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * A save writes records server-side (the document's comprehension), so reads kept from before it are dropped.
	 * @param {(pError?: Error) => void} fCallback
	 */
	onBeforeSaveDataAsync(fCallback)
	{
		libSharedEntityRead.invalidate(this.pict);
		return super.onBeforeSaveDataAsync(fCallback);
	}

	/**
	 * Drops reads taken while the save was in flight.
	 * @param {(pError?: Error) => void} fCallback
	 */
	onAfterSaveDataAsync(fCallback)
	{
		libSharedEntityRead.invalidate(this.pict);
		return super.onAfterSaveDataAsync(fCallback);
	}

	/**
	 * @typedef {Object} ReadRequest
	 * @property {string} url - The URL to issue the request against.
	 * @property {'GET'|'POST'} method - The HTTP method to use.
	 * @property {Object|null} body - The JSON body for a POST `/Query` request, or null for a GET.
	 */

	/**
	 * Convert a meadow GET read URL into either a passthrough GET or, when the URL
	 * exceeds `pThreshold`, an equivalent `POST <prefix><Entity>s/Query` request so a
	 * large IN-list filter doesn't blow the URI length limit. The POST body envelope
	 * maps onto the same GET read handlers (meadow-endpoints `/Query`); mode is selected
	 * by `{ Count, Distinct, Lite }` flags with precedence Count > Distinct > Lite > Reads.
	 *
	 * Pure: no `this.pict` / DOM / select2, so it is unit-tested directly. Short URLs pass
	 * through as GET to preserve response caching for the common case. Any URL that can't be
	 * parsed (no `/FilteredTo/` segment) falls back to a GET passthrough (fail-safe).
	 *
	 * Encoding: the GET router URL-decodes the `:Filter` / `:ExtraColumns` / `:Columns` path
	 * params once before the handler reads `params.*`, so those segments are `decodeURIComponent`'d
	 * into the body — a `LK~%25term%25` path segment becomes the `LK~%term%` the handler would see.
	 *
	 * @param {string} pURL - The meadow GET read URL (e.g. `<prefix><Entity>s/Lite/FilteredTo/<filter>/<begin>/<cap>`).
	 * @param {number} [pThreshold=1800] - URL length at/under which the GET is passed through unchanged.
	 * @return {ReadRequest} The request descriptor.
	 */
	_buildReadRequest(pURL, pThreshold)
	{
		const tmpThreshold = pThreshold ?? 1800;
		if (!pURL || pURL.length <= tmpThreshold)
		{
			return { url: pURL, method: 'GET', body: null };
		}
		// Robust parse (not one fragile regex): split on the FilteredTo delimiter; the head carries
		// the entity + read-mode segments, the tail carries the filter and optional /begin/cap.
		const tmpParts = pURL.split('/FilteredTo/');
		if (tmpParts.length !== 2)
		{
			return { url: pURL, method: 'GET', body: null };
		}
		const tmpHead = tmpParts[0];
		const tmpTail = tmpParts[1];

		// From the tail, peel a trailing /<digits>/<digits> as Begin/Cap; the remainder is the Filter
		// (meadow filters use `~`, never a literal `/`, so re-joining any interior slashes is safe).
		let tmpFilterSegment = tmpTail;
		let tmpBegin = null;
		let tmpCap = null;
		const tmpTailSegments = tmpTail.split('/').filter((pSegment) => pSegment.length > 0);
		if (tmpTailSegments.length >= 2 &&
			/^\d+$/.test(tmpTailSegments[tmpTailSegments.length - 2]) &&
			/^\d+$/.test(tmpTailSegments[tmpTailSegments.length - 1]))
		{
			tmpCap = parseInt(tmpTailSegments.pop(), 10);
			tmpBegin = parseInt(tmpTailSegments.pop(), 10);
			tmpFilterSegment = tmpTailSegments.join('/');
		}

		// From the head, peel the trailing read-mode segment(s). The head is
		// `<prefix><Entity>s` optionally followed by `/Lite`, `/LiteExtended/<cols>`,
		// `/Distinct/<cols>`, or `/Count`. Everything to the left of the mode is the
		// entity path; the POST endpoint is `<entity-path>/Query`.
		const tmpHeadSegments = tmpHead.split('/');
		const tmpBody = {};
		let tmpEntitySegmentCount = tmpHeadSegments.length;
		const tmpLast = tmpHeadSegments[tmpHeadSegments.length - 1];
		const tmpSecondLast = tmpHeadSegments[tmpHeadSegments.length - 2];
		if (tmpLast === 'Lite')
		{
			tmpBody.Lite = true;
			tmpEntitySegmentCount = tmpHeadSegments.length - 1;
		}
		else if (tmpLast === 'Count')
		{
			tmpBody.Count = true;
			tmpEntitySegmentCount = tmpHeadSegments.length - 1;
		}
		else if (tmpSecondLast === 'LiteExtended')
		{
			tmpBody.Lite = true;
			tmpBody.ExtraColumns = decodeURIComponent(tmpLast);
			tmpEntitySegmentCount = tmpHeadSegments.length - 2;
		}
		else if (tmpSecondLast === 'Distinct')
		{
			tmpBody.Distinct = true;
			tmpBody.Columns = decodeURIComponent(tmpLast);
			tmpEntitySegmentCount = tmpHeadSegments.length - 2;
		}
		const tmpEntityPath = tmpHeadSegments.slice(0, tmpEntitySegmentCount).join('/');

		tmpBody.Filter = decodeURIComponent(tmpFilterSegment);
		// Count ignores pagination (it counts the whole filtered query); every other mode forwards it.
		if (!tmpBody.Count && tmpBegin !== null)
		{
			tmpBody.Begin = tmpBegin;
			tmpBody.Cap = tmpCap;
		}

		return { url: `${tmpEntityPath}/Query`, method: 'POST', body: tmpBody };
	}

	/**
	 * Float a set of ids to the front of an option list, in the order given, leaving everything else in place.
	 * Ids absent from the page are skipped rather than synthesized — the page is already scoped, so an id that
	 * did not survive the filters must not be reintroduced by the ordering.
	 *
	 * @param {Array<{id: any}>} pOptions - the composed option list for this page.
	 * @param {Array<any>} pPriorityIDs - ids to float, most-preferred first.
	 * @return {Array<{id: any}>} The reordered list (a new array; the input is not mutated).
	 */
	applyPriorityOrder(pOptions, pPriorityIDs)
	{
		if (!Array.isArray(pOptions) || !Array.isArray(pPriorityIDs) || pPriorityIDs.length < 1)
		{
			return pOptions;
		}
		const tmpPinned = [];
		for (const tmpID of pPriorityIDs)
		{
			const tmpMatch = pOptions.find((pOption) => String(pOption.id) === String(tmpID));
			if (tmpMatch && tmpPinned.indexOf(tmpMatch) < 0)
			{
				tmpPinned.push(tmpMatch);
			}
		}
		if (tmpPinned.length < 1)
		{
			return pOptions;
		}
		return tmpPinned.concat(pOptions.filter((pOption) => tmpPinned.indexOf(pOption) < 0));
	}

	/**
	 * Helper to standardize building select2 options objects.
	 *
	 * @param {string} pEntityType - The entity type to get options for.
	 * @param {Select2AjaxOptions} pOptions - The options to use when getting the options.
	 *
	 * @return {import('select2').Options} - The options for select2.
	 */
	getOptionsAjax(pEntityType, pOptions)
	{
		const pageSize = pOptions.PageSize || 50;
		const input = pOptions.Input || {};
		const tmpProvider = this;
		// url() receives Select2 query params; transport() receives the ajax-OPTIONS object (a
		// different object). Bridge the page-1 mandatory-only fallback URL keyed by the full URL url()
		// returns (= options.url in transport), read and cleared once per request.
		const tmpBackoffFallbacks = {};
		// Set per query by url(): did a SCOPE filter (beyond the search term) apply? Drives the filter-aware
		// no-results message so the user learns a filter zeroed the picker and can reach for "Show all".
		let tmpScopeFilterApplied = false;
		const searchFields = pOptions.SearchFields || (pEntityType === 'User' ? [ 'NameFirst', 'NameLast' ] : [ 'Name' ]);
		const getUserFacingRecordID = pOptions.GetRecordUserFacingID || (() => null);
		const recordToID = pOptions.GetRecordID ?? ((pEntity) => pEntity[`ID${pEntityType}`]);
		const recordToText = pOptions.GetRecordName ?? ((pEntity) =>
		{
			let baseName = pOptions.EntityDisplayField ? pEntity[pOptions.EntityDisplayField] : pEntityType === 'User' ? `${ pEntity.NameFirst } ${ pEntity.NameLast }` : pEntity.Name || `Unnamed ${pEntityType} #${pEntity[`ID${pEntityType}`]}`;
			if (pEntity.JoinName)
			{
				baseName = pOptions.JoinEntityFirst ? `${ pEntity.JoinName } - ${ baseName }` : `${ baseName } - ${ pEntity.JoinName }`;
			}
			const recordID = getUserFacingRecordID(pEntity);
			// Suppress the tag pill when it's identical to the name (e.g. Labs whose code IS their name) —
			// the compound would render a redundant "(X) X". Fall through to the bare name (matches the PDF).
			if (recordID && String(recordID).trim() !== String(baseName).trim())
			{
				return `${recordID}|~|${baseName}`;
			}
			return baseName;
		});
		const getPinnedItem = pOptions.GetPinnedItem ?? (() => null);
		// Pin-to-top: ids floated above the rest of the first BROWSE page, in the given order. Nothing is
		// removed, and a search term defers entirely to natural relevance so a pin never hides a typed-for
		// record — the same contract pict-section-picker's PriorityValues carries on the Picker stack.
		const getPriorityIDs = pOptions.GetPriorityIDs ?? (() => []);
		const handleSearchFilters = pOptions.HandleSearchFilters ?? ((pFilters, pParams) => pFilters);
		// NOTE: Select2 Option Documentation: https://select2.org/configuration/options-api
		/** @type {import('select2').Options} */
		const tmpSelectOptions =
		{
			minimumInputLength: pOptions.MinimumInputLengthForSearch ?? this.pict.views.HeadlightPanels?.MinimumInputLengthForSearch ?? 0,
			tags: pOptions.Tags ?? false,
			allowClear: pOptions.AllowClear ?? true,
			placeholder: pOptions.SearchPlaceholder || input.PictForm?.Placeholder || pOptions.SearchFallbackPlaceholder || `Select ${pEntityType} records...`,
			width: pOptions.Width ?? '100%',
			disabled: pOptions.Disabled ?? false,
			multiple: pOptions.Multiple ?? false,
			language:
			{
				searching: function()
				{
					return 'Searching...';
				},
				noResults: function()
				{
					if (tmpScopeFilterApplied)
					{
						return input.PictForm?.FilteredNoResultsMessage ||
							(input.PictForm?.OverrideField ?
								'No matches with the current filter — turn on "Show all" to see everything.' :
								'No matches with the current filter.');
					}
					return 'No results found.';
				},
				loadingMore: function()
				{
					return 'Loading more results...';
				},
				inputTooShort: function(args)
				{
					return 'Please enter at least ' + args.minimum + ' characters to see results.';
				},
			},
			escapeMarkup: function(text)
			{
				if (text && text.includes('|~|'))
				{
					const tempNumber = text.split('|~|')[0]?.trim();
					const tempName = text.split('|~|')[1]?.trim();
					return _Pict.providers.Select2Util.buildSelect2CompoundDisplayTemplate(tempNumber, tempName, pOptions.Multiple, pOptions.EntityTagLast);
				}
				return text;
			},
			ajax:
			{
				cache: true,
				method: 'GET',
				url: function(params)
				{
					const page = params.page || 1;
					const tmpSortClause = searchFields.map((f) => `FSF~${ f }~ASC~0`).join('~');
					const buildURL = (pFilterList) =>
					{
						let tmpURL = `${pOptions.URLPrefix || ''}${pEntityType}s/`;
						if (pFilterList.length > 0)
						{
							tmpURL += pOptions?.ApplySort ? `FilteredTo/${pFilterList.join('~')}~${tmpSortClause}/` : `FilteredTo/${pFilterList.join('~')}/`;
						}
						else if (pOptions?.ApplySort)
						{
							tmpURL += `FilteredTo/${tmpSortClause}/`;
						}
						tmpURL += `${(page - 1) * pageSize}/${pageSize}`;
						return tmpURL;
					};
					// Mandatory filters (search term, intrinsic subclass scope, project scope) always apply.
					// Back-off-able filters (BackOff static/dynamic, spec-year) collect separately so the
					// transport can drop them on an empty-result retry, and the override drops them up-front.
					let filters = [];
					const tmpBackoff = [];
					if (params.term && searchFields.length > 0)
					{
						const tmpTerms = params.term.split(' ').map((pTerm) => pTerm.trim()).filter((pTerm) => pTerm.length > 0);
						if (tmpTerms.length > 0)
						{
							filters = tmpTerms.flatMap((tmpTerm) =>
							{
								const tmpFieldFilters = searchFields.map((pField) => `FBVOR~${pField}~LK~${encodeURIComponent(`%${tmpTerm}%`)}%25`);
								tmpFieldFilters.unshift('FOP~0~(~0');
								tmpFieldFilters.push('FCP~0~)~0');
								return tmpFieldFilters;
							});
						}
					}
					filters = handleSearchFilters(filters, params, tmpBackoff) || filters;
					tmpProvider.applyStaticFilters(filters, input, tmpBackoff);
					// pOptions.RowIndex / pOptions.View are set for tabular columns (see
					// MaterialsEntitySelector.initializeSelect2) so FromRowField narrows per-row.
					tmpProvider.applyDynamicFilters(filters, input, pOptions.RowIndex, pOptions.View, tmpBackoff);
					// pOptions.RowIndex must be threaded here too: a tabular picker persists its Show All
					// state per row (<Hash>__ShowAll_<row>), so reading the flat address left every
					// per-row toggle inert on this path.
					const tmpEffectiveBackoff = tmpProvider.isFilterOverridden(input, pOptions.RowIndex) ? [] : tmpBackoff;
					// A scope filter applied if any clause is not a search-term clause (FBVOR/FOP/FCP), or a
					// back-off clause is in effect — read by noResults to explain an empty/zeroed picker.
					tmpScopeFilterApplied = tmpEffectiveBackoff.length > 0 || filters.some((tmpClause) => tmpClause.indexOf('FBVOR~') !== 0 && tmpClause.indexOf('FOP~') !== 0 && tmpClause.indexOf('FCP~') !== 0);
					// Mandatory-only fallback the transport retries when the fully-filtered query is empty.
					// page-1 fallback is recorded against the returned full URL just below (read in transport).
					const tmpFullURL = buildURL(filters.concat(tmpEffectiveBackoff));
					if (tmpEffectiveBackoff.length > 0 && page === 1)
					{
						tmpBackoffFallbacks[tmpFullURL] = buildURL(filters);
					}
					return tmpFullURL;
				},
				xhrFields:
				{
					withCredentials: true,
				},
				dataType: 'json',
				transport: (params, success, failure) =>
				{
					let aborted = false;
					let tmpActiveRequest = null;
					const tmpFallbackURL = tmpBackoffFallbacks[params.url];
					delete tmpBackoffFallbacks[params.url];
					// Decorate a response with JoinEntity display names (when configured) and resolve.
					const finishWithResponse = async (pResponse) =>
					{
						if (pOptions?.JoinEntity && pResponse?.[0]?.hasOwnProperty(pOptions.JoinField))
						{
							const ids = [];
							for (const r of pResponse)
							{
								if (r[pOptions.JoinField])
								{
									ids.push(r[pOptions.JoinField]);
								}
							}
							if (ids.length)
							{
								try
								{
									const joinedEntities = await this.getEntitySetAsync(pOptions?.JoinEntity, `FBL~ID${ pOptions?.JoinEntity }~INN~${ ids.join(',') }`);
									if (joinedEntities?.length)
									{
										const idMap = {};
										for (const j of joinedEntities)
										{
											idMap[j[`ID${ pOptions?.JoinEntity }`]] = j;
										}
										const newResponse = [];
										for (const r of pResponse)
										{
											const joinEntityObj = idMap[r[pOptions.JoinField]];
											let joinName = null;
											if (joinEntityObj)
											{
												if (pOptions.JoinEntityDisplayField)
												{
													joinName = joinEntityObj[pOptions.JoinEntityDisplayField];
												}
												else if (pOptions.JoinEntity === 'User')
												{
													joinName = `${joinEntityObj.NameFirst} ${joinEntityObj.NameLast}`;
												}
												else
												{
													joinName = joinEntityObj.Name || '';
												}
											}
											r.JoinName = joinName;
											newResponse.push(r);
										}
										pResponse = newResponse;
									}
								}
								catch (e)
								{
									console.log(`Error fetching joined ${ pOptions?.JoinEntity }:`, e);
									return failure();
								}
							}
						}
						if (!aborted)
						{
							success(pResponse || []);
						}
					};
					const fetchURL = (pURL, fOnBody, fOnError) =>
					{
						// Oversized read URLs (large IN-list filters) fall back to POST /Query so they
						// don't blow the URI length limit; short URLs stay GET to preserve caching.
						const tmpReq = this._buildReadRequest(pURL);
						const tmpHandler = (pError, pResponse) =>
						{
							if (pError || pResponse?.Error)
							{
								console.log(`Error fetching ${ pURL }:`, pError || pResponse?.Error);
								return fOnError();
							}
							fOnBody(pResponse);
						};
						if (tmpReq.method === 'POST')
						{
							tmpActiveRequest = this.pict.HeadlightRestClient.postJSON(tmpReq.url, tmpReq.body, tmpHandler);
						}
						else
						{
							tmpActiveRequest = this.pict.HeadlightRestClient.getJSON(tmpReq.url, tmpHandler);
						}
					};
					fetchURL(params.url, (pResponse) =>
					{
						// Back-off: a fully-filtered FIRST page that returns nothing retries without the
						// back-off-able filters, so the picker shows its full set instead of an empty dropdown.
						// Only the first page backs off — on later pages an empty body means "no more results",
						// not "the filter emptied it", so retrying there would surface out-of-filter rows.
						if (Array.isArray(pResponse) && pResponse.length === 0 && tmpFallbackURL)
						{
							fetchURL(tmpFallbackURL, (pFallback) => finishWithResponse(pFallback), failure);
							return;
						}
						finishWithResponse(pResponse);
					}, failure);

					return { abort: () =>
					{
						aborted = true;
						if (tmpActiveRequest)
						{
							tmpActiveRequest.abort();
						}
					} };
				},
				processResults: function(data, params)
				{
					let formattedData = [];
					const pageSize = 50;
					params.page = params.page || 1;
					if (data && data.length > 0)
					{
						formattedData = data.map((pItem) =>
						{
							const item =
							{
								id: recordToID(pItem),
								text: recordToText(pItem),
							};
							return item;
						});
						if (params.page === 1)
						{
							if (!params.term)
							{
								formattedData = tmpProvider.applyPriorityOrder(formattedData, getPriorityIDs());
							}
							const pinnedItem = getPinnedItem();
							if (pinnedItem)
							{
								formattedData = formattedData.filter((pItem) => pItem.id != pinnedItem.id);
								formattedData.unshift(pinnedItem);
							}
						}
					}
					const pageInfo =
					{
						results: formattedData,
						pagination:
						{
							more: data.length >= pageSize,
						},
					};
					return pageInfo;
				},
			},
		};
		return tmpSelectOptions;
	}

	/**
	 * Fetch a single entity record. The record is returned exactly as the server sent it; callers that also
	 * need display text use getEntityWithDisplayAsync (record snapshots held in form data are persisted
	 * verbatim, so nothing may be decorated onto a fetched record here).
	 *
	 * Identical lookups share one request, and a found record is reused for a short window
	 * (`_SharedEntityRead`); pass `BypassCache` to skip the reuse window (a request already in flight is still shared).
	 *
	 * @param {string} pEntity - The entity type to get.
	 * @param {number|string} pIDEntity - The ID of the entity to get
	 * @param {Record<string, any>} pOptions - Options for getting the entity.
	 *
	 * @return {Promise<Record<string, any>>} - The entity record.
	 */
	async getEntityAsync(pEntity, pIDEntity, pOptions = {})
	{
		return new Promise((resolve, reject) =>
		{
			const tmpIdentityField = pOptions.IdentityField || `ID${pEntity}`;
			if (libSharedEntityRead.isEmptyIdentityValue(tmpIdentityField, pIDEntity))
			{
				return reject(new Error(`No ${pEntity} found with ${tmpIdentityField} = ${pIDEntity}`));
			}
			const tmpURL = `${pEntity}s/FilteredTo/FBV~${tmpIdentityField}~EQ~${encodeURIComponent(pIDEntity)}`;
			libSharedEntityRead.readJSON(this.pict, tmpURL, { ReuseResult: !pOptions.BypassCache }, (pError, pResult) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				if (pResult.length < 1)
				{
					return reject(new Error(`No ${pEntity} found with ${tmpIdentityField} = ${pIDEntity}`));
				}
				if (pResult.length > 1)
				{
					this.pict.log.warn(`Multiple ${pEntity} records found with ${tmpIdentityField} = ${pIDEntity}, returning the first result.`);
				}
				resolve(pResult[0]);
			});
		});
	}

	/**
	 * Fetch a single entity record along with the display text for it.
	 *
	 * @param {string} pEntity - The entity type to get.
	 * @param {number|string} pIDEntity - The ID of the entity to get
	 * @param {Record<string, any>} pOptions - Options for getting the entity.
	 *
	 * @return {Promise<EntityDisplayResult>} - The entity record and its display name.
	 */
	async getEntityWithDisplayAsync(pEntity, pIDEntity, pOptions = {})
	{
		const tmpRecord = await this.getEntityAsync(pEntity, pIDEntity, pOptions);
		return { Record: tmpRecord, DisplayName: await this.getEntityDisplayNameAsync(pEntity, tmpRecord, pOptions) };
	}

	/**
	 * Resolve the display text for an entity record, loading the configured JoinEntity when one applies.
	 *
	 * @param {string} pEntity - The entity type the record belongs to.
	 * @param {Record<string, any>} pRecord - The entity record.
	 * @param {Record<string, any>} pOptions - Options for the display name (EntityDisplayField, JoinEntity, ...).
	 *
	 * @return {Promise<string>} - The display name for the record.
	 */
	async getEntityDisplayNameAsync(pEntity, pRecord, pOptions = {})
	{
		const tmpDisplayName = this.buildEntityDisplayName(pEntity, pRecord, pOptions);
		if (pOptions.JoinEntity && pRecord?.[pOptions.JoinField])
		{
			const tmpJoinedRecord = await this.getEntityAsync(pOptions.JoinEntity, pRecord[pOptions.JoinField]);
			const tmpJoinedDisplayName = this.buildEntityDisplayName(pOptions.JoinEntity, tmpJoinedRecord, { EntityDisplayField: pOptions.JoinEntityDisplayField });
			return pOptions.JoinEntityFirst ? `${ tmpJoinedDisplayName } - ${ tmpDisplayName }` : `${ tmpDisplayName } - ${ tmpJoinedDisplayName }`;
		}
		return tmpDisplayName;
	}

	/**
	 * Compute the display text for an entity record. Pure -- the record is never written to.
	 *
	 * @param {string} pEntity - The entity type the record belongs to.
	 * @param {Record<string, any>} pRecord - The entity record.
	 * @param {Record<string, any>} pOptions - Options for the display name (EntityDisplayField).
	 *
	 * @return {string} - The display name for the record.
	 */
	buildEntityDisplayName(pEntity, pRecord, pOptions = {})
	{
		// The resolution order (dotted paths, the Name-less User table, …) lives in the shared helper so the
		// Picker stack's Read/Print display resolves identically; this chain's only addition is the legacy
		// "Unnamed <Entity> #<id>" placeholder for a record with nothing to show.
		return libEntityDisplayName.resolveEntityDisplayName(pEntity, pRecord, pOptions,
			() => `Unnamed ${pEntity} #${pRecord[`ID${pEntity}`]}`);
	}

	/**
	 * @param {string} pEntity - The entity type to get.
	 * @param {string} pFilter - The filter to apply to the entity set.
	 *
	 * @return {Promise<Array<Record<string, any>>>} - The entity record.
	 */
	async getEntitySetAsync(pEntity, pFilter)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getEntitySet(pEntity, pFilter, (pError, pResult) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pResult);
			});
		});
	}

	/**
	 * Builds a Select2 display template with tag and name. Supports both singluar and multiple displays.
	 * The tag can be placed at the start or end as an additional option.
	 *
	 * @param {string} pTag - The tag to display.
	 * @param {string} pName - The name to display.
	 * @param {boolean} [pMultiple=false] - Whether this is for multiple selection.
	 * @param {boolean} [pTagAtEnd=false] - Whether to put the tag at the end.
	 * @return {string} - The HTML template string.
	 */
	buildSelect2CompoundDisplayTemplate(pTag, pName, pMultiple = false, pTagAtEnd = false)
	{
		return (
			`<span class="select2-${pMultiple ? 'multiple' : 'single'}-entity-selection">
				${pTagAtEnd ? `<span class="select2-entity-name">${pName}</span><span class="tag select2-entity-tag">${pTag}</span>` : `<span class="tag select2-entity-tag">${pTag}</span><span class="select2-entity-name">${pName}</span>`}
			</span>`
		);
	}
}

module.exports = Select2UtilProvider;
// Joins the app save-data lifecycle so a save drops the shared entity reads (onBefore/onAfterSaveDataAsync).
module.exports.default_configuration = { AutoSaveDataWithApp: true };
